import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { ArrowLeft, Plus, Pencil, Trash2, X, Search, Save, Star, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function PriceItemRow({ item, editing, setEditing, onDelete }) {
  const [form, setForm] = useState({
    description: item.description, spec: item.spec || "", unit: item.unit,
    rate: item.rate, gst_percent: item.gst_percent, category: item.category || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.priceItemUpdate(item.id, {
        ...form, rate: Number(form.rate), gst_percent: Number(form.gst_percent),
      });
      toast.success("Updated");
      setEditing(null);
    } catch { toast.error("Could not update"); }
    finally { setSaving(false); }
  };

  if (editing !== item.id) {
    return (
      <tr className="border-t border-anvi-100 hover:bg-anvi-50/40" data-testid={`price-row-${item.id}`}>
        <td className="px-4 py-2.5">
          <div className="text-sm text-anvi-900">{item.description}</div>
          {item.spec && <div className="text-xs text-anvi-500">{item.spec}</div>}
        </td>
        <td className="px-4 py-2.5 text-xs text-anvi-500">{item.category || "—"}</td>
        <td className="px-4 py-2.5 text-sm text-anvi-700">{item.unit}</td>
        <td className="px-4 py-2.5 text-sm text-right tabular-nums text-anvi-900 font-medium">{inr(item.rate)}</td>
        <td className="px-4 py-2.5 text-sm text-right tabular-nums text-anvi-600">{item.gst_percent}%</td>
        <td className="px-4 py-2.5 text-right">
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => setEditing(item.id)} className="text-anvi-500 hover:text-anvi-terracotta p-1" title="Edit" data-testid={`edit-price-${item.id}`}>
              <Pencil size={13} strokeWidth={1.5} />
            </button>
            <button onClick={() => onDelete(item)} className="text-anvi-400 hover:text-red-600 p-1" title="Delete" data-testid={`delete-price-${item.id}`}>
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-anvi-100 bg-anvi-50/60" data-testid={`price-row-edit-${item.id}`}>
      <td className="px-2 py-1.5">
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-2 py-1 text-sm border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
        <input value={form.spec} onChange={(e) => setForm({ ...form, spec: e.target.value })} placeholder="Spec" className="w-full mt-1 px-2 py-1 text-xs border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
      </td>
      <td className="px-2 py-1.5">
        <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-2 py-1 text-xs border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
      </td>
      <td className="px-2 py-1.5">
        <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-2 py-1 text-sm border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
      </td>
      <td className="px-2 py-1.5">
        <input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className="w-full px-2 py-1 text-sm text-right tabular-nums border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
      </td>
      <td className="px-2 py-1.5">
        <input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="w-full px-2 py-1 text-sm text-right tabular-nums border border-anvi-200 rounded-sm focus:outline-none focus:border-anvi-terracotta" />
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center justify-end gap-1">
          <button onClick={save} disabled={saving} className="text-emerald-600 hover:text-emerald-800 p-1" title="Save" data-testid={`save-price-${item.id}`}>
            <Save size={13} strokeWidth={1.5} />
          </button>
          <button onClick={() => setEditing(null)} className="text-anvi-500 hover:text-anvi-900 p-1" title="Cancel">
            <X size={13} strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function AddPriceItemForm({ vendorId, onAdded }) {
  const [form, setForm] = useState({ description: "", spec: "", unit: "nos", rate: "", gst_percent: 18, category: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Description required"); return; }
    setSaving(true);
    try {
      const res = await api.priceItemCreate(vendorId, {
        ...form, rate: Number(form.rate) || 0, gst_percent: Number(form.gst_percent) || 0,
      });
      toast.success("Added to rate card");
      onAdded(res.data);
      setForm({ description: "", spec: "", unit: "nos", rate: "", gst_percent: 18, category: "" });
    } catch { toast.error("Could not add"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-anvi-50/60 border-t border-anvi-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2">
          <label className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 block mb-1">Description</label>
          <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                 data-testid="new-price-desc"
                 className="w-full px-2 py-1.5 text-sm border border-anvi-200 rounded-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 block mb-1">Category</label>
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                 className="w-full px-2 py-1.5 text-sm border border-anvi-200 rounded-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 block mb-1">Unit</label>
          <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                 data-testid="new-price-unit"
                 className="w-full px-2 py-1.5 text-sm border border-anvi-200 rounded-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 block mb-1">Rate ₹</label>
          <input type="number" required value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })}
                 data-testid="new-price-rate"
                 className="w-full px-2 py-1.5 text-sm text-right tabular-nums border border-anvi-200 rounded-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 block mb-1">GST %</label>
          <input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })}
                 className="w-full px-2 py-1.5 text-sm text-right tabular-nums border border-anvi-200 rounded-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button type="submit" disabled={saving} data-testid="add-price-btn"
                className="flex items-center gap-1.5 text-sm bg-anvi-terracotta text-white px-4 py-1.5 rounded-md hover:bg-anvi-terracotta-dark transition-colors disabled:opacity-60">
          <Plus size={13} strokeWidth={1.5} /> {saving ? "Adding…" : "Add to rate card"}
        </button>
      </div>
    </form>
  );
}

export default function VendorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [v, p] = await Promise.all([api.vendor(id), api.priceList(id)]);
      setVendor(v.data);
      setItems(p.data || []);
    } catch { toast.error("Could not load vendor"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => [x.description, x.spec, x.category, x.unit].some((f) => (f || "").toLowerCase().includes(q)));
  }, [items, query]);

  const handleAdded = (item) => setItems((prev) => [...prev, item]);
  const handleDelete = async (item) => {
    if (!window.confirm(`Remove "${item.description}" from rate card?`)) return;
    try {
      await api.priceItemDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Removed from rate card");
    } catch { toast.error("Could not delete"); }
  };

  if (!vendor) return <div className="p-10 text-anvi-500">Loading vendor…</div>;

  const rating = Number(vendor.rating) || 0;

  return (
    <div className="px-10 pt-4 pb-10 max-w-[1400px] animate-fade-in" data-testid="vendor-detail-page">
      <Link to="/vendors" className="text-xs tracking-[0.15em] uppercase text-anvi-500 hover:text-anvi-terracotta flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to vendors
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendor summary */}
        <div className="lg:col-span-1 bg-white border border-anvi-200 rounded-md p-6 h-fit">
          <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider bg-anvi-100 text-anvi-700">{vendor.category}</span>
          <h1 className="font-heading text-3xl text-anvi-900 mt-2">{vendor.name}</h1>
          {vendor.contact_name && <div className="text-sm text-anvi-700 mt-1">{vendor.contact_name}</div>}
          <div className="mt-4 space-y-2 text-sm">
            {vendor.phone && <div className="flex items-center gap-2 text-anvi-700"><Phone size={13} strokeWidth={1.5} /> {vendor.phone}</div>}
            {vendor.email && <div className="flex items-center gap-2 text-anvi-700"><Mail size={13} strokeWidth={1.5} /> {vendor.email}</div>}
            {vendor.gstin && <div className="font-mono text-xs text-anvi-500">GSTIN: {vendor.gstin}</div>}
            {vendor.address && <div className="text-xs text-anvi-500 mt-2">{vendor.address}</div>}
          </div>
          <div className="mt-4 pt-4 border-t border-anvi-100 flex items-center gap-1 text-amber-500">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} size={14} strokeWidth={1.5} fill={i <= Math.round(rating * 2) / 2 ? "currentColor" : "none"} />
            ))}
            <span className="text-xs text-anvi-500 ml-1 tabular-nums">{rating.toFixed(1)} rating</span>
          </div>
          {vendor.notes && (
            <div className="mt-4 pt-4 border-t border-anvi-100">
              <div className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 mb-1">Notes</div>
              <div className="text-sm text-anvi-700 whitespace-pre-line">{vendor.notes}</div>
            </div>
          )}
        </div>

        {/* Rate card */}
        <div className="lg:col-span-2 bg-white border border-anvi-200 rounded-md overflow-hidden">
          <div className="px-5 py-4 border-b border-anvi-200 flex items-center justify-between bg-anvi-50/60">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Rate card</div>
              <div className="font-heading text-xl text-anvi-900 mt-0.5">{items.length} item{items.length === 1 ? "" : "s"} in catalogue</div>
            </div>
            <div className="relative w-72">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-anvi-400" strokeWidth={1.5} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search catalogue…"
                     data-testid="price-search"
                     className="w-full pl-8 pr-3 py-1.5 border border-anvi-200 rounded-md text-sm bg-white focus:outline-none focus:border-anvi-terracotta" />
            </div>
          </div>
          {items.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-anvi-500" data-testid="price-empty">
              No items in this vendor's rate card yet. Add one below and it will show up in the PO builder.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-white">
                <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500">
                  <th className="px-4 py-2 text-left font-medium">Description</th>
                  <th className="px-4 py-2 text-left font-medium w-32">Category</th>
                  <th className="px-4 py-2 text-left font-medium w-16">Unit</th>
                  <th className="px-4 py-2 text-right font-medium w-24">Rate</th>
                  <th className="px-4 py-2 text-right font-medium w-16">GST%</th>
                  <th className="px-4 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <PriceItemRow key={item.id} item={item} editing={editingId} setEditing={setEditingId} onDelete={handleDelete} />
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-anvi-500">No items match "{query}".</td></tr>
                )}
              </tbody>
            </table>
          )}
          <AddPriceItemForm vendorId={id} onAdded={handleAdded} />
        </div>
      </div>
    </div>
  );
}
